import { FOLDER_COLOR_KEYS, type FolderColor, type PageKind } from '@regia/shared';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ApiError, errorMessage } from '../../lib/api';
import { Button, Field, Input, Segmented, Sheet } from '../../ui';
import { useCreatePage } from './api';
import { ColorPicker } from './ColorPicker';

export function NewPageSheet({
  open,
  onOpenChange,
  count,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  count: number;
}) {
  const navigate = useNavigate();
  const create = useCreatePage();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<PageKind>('cliente');
  const [color, setColor] = useState<FolderColor>(
    FOLDER_COLOR_KEYS[count % FOLDER_COLOR_KEYS.length]!,
  );
  const [error, setError] = useState<string | undefined>();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    try {
      const page = await create.mutateAsync({ name, kind, color });
      toast.success('Scheda creata');
      onOpenChange(false);
      setName('');
      navigate(`/schede/${page.slug}/info`);
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields?.name ?? err.message) : errorMessage(err));
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Nuova scheda"
      description="Una scheda per ogni pagina o cliente: dentro trovi contenuti, info e accessi."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button type="submit" form="new-page" loading={create.isPending} disabled={!name.trim()}>
            Crea scheda
          </Button>
        </>
      }
    >
      <form id="new-page" onSubmit={submit} className="flex flex-col gap-5 pt-2" noValidate>
        <Field label="Nome" error={error}>
          {(id, describedBy) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Per esempio Nefer Benessere"
              autoFocus
              invalid={Boolean(error)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-ink">Di chi è</span>
          <Segmented
            label="Di chi è"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'cliente', label: 'Di un cliente' },
              { value: 'mio', label: 'Mia' },
            ]}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-ink">Colore della cartellina</span>
          <ColorPicker value={color} onChange={setColor} />
        </div>
      </form>
    </Sheet>
  );
}
